/**
 * 统计卡片（旧 components/dashboard/StatsCard.vue）：图标、标题、数值、趋势、说明
 */
import type { ReactNode } from 'react'
import { Card, Skeleton, Typography, theme } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, MinusOutlined } from '@ant-design/icons'
import styles from './StatsCard.module.css'

interface StatsCardProps {
  title: string
  value: number | string
  icon: ReactNode
  // 图标底色，取自主题色板
  accentColor: string
  description?: string
  // 百分比趋势；不传时不显示
  trend?: number
  loading?: boolean
}

/** 数值显示与旧版一致：≥1 万显示「x.x万」，≥1000 显示「x.xk」，其余加千分位 */
export function formatStatValue(value: number | string): string {
  if (typeof value === 'string') return value
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
  return value.toLocaleString('zh-CN')
}

function TrendValue({ trend }: Readonly<{ trend: number }>) {
  const { token } = theme.useToken()
  let color = token.colorTextTertiary
  let icon = <MinusOutlined />
  if (trend > 0) {
    color = token.colorSuccess
    icon = <ArrowUpOutlined />
  } else if (trend < 0) {
    color = token.colorError
    icon = <ArrowDownOutlined />
  }
  return (
    <span className={styles.trend} style={{ color }}>
      {icon}
      {Math.abs(trend)}%
    </span>
  )
}

export function StatsCard({ title, value, icon, accentColor, description, trend, loading = false }: Readonly<StatsCardProps>) {
  const { token } = theme.useToken()
  return (
    <Card variant="borderless" className={styles.card}>
      <div className={styles.header}>
        <div className={styles.icon} style={{ backgroundColor: accentColor, color: token.colorWhite }}>
          {icon}
        </div>
        {trend !== undefined && <TrendValue trend={trend} />}
      </div>
      <Typography.Text type="secondary">{title}</Typography.Text>
      <div className={styles.value}>
        {loading ? <Skeleton.Input active size="large" /> : formatStatValue(value)}
      </div>
      {description && <div className={styles.description}>{description}</div>}
    </Card>
  )
}
